import { useSession } from "@/services/auth0/session";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

export interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  roles: string[];
  createdAt?: string;
  connections?: { providerId: string; provider: string }[];
  customer?: { id: string } | null;
  operator?: {
    id: string;
    dispatchCenterId?: string | null;
    depotId?: string | null;
  } | null;
}

export interface CreateUserInput {
  email: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
}

export interface UpdateUserInput {
  userId: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
}

export interface AssignOperatorInput {
  userId: string;
  dispatchCenterId: string;
}

export const useUsers = () => {
  const { token } = useSession();

  return useQuery<User[]>({
    queryKey: ["users"],
    queryFn: async () => {
      const { data } = await axios.get<User[]>(
        `${import.meta.env.VITE_API_URL}/api/v2/users`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    enabled: !!token,
  });
};

export const useCreateUser = () => {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateUserInput) => {
      const { data } = await axios.post<User>(
        `${import.meta.env.VITE_API_URL}/api/v2/users`,
        input,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
};

export const useUpdateUser = () => {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, ...input }: UpdateUserInput) => {
      const { data } = await axios.patch<User>(
        `${import.meta.env.VITE_API_URL}/api/v2/users/${userId}`,
        input,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
};

export const useAssignOperator = () => {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, dispatchCenterId }: AssignOperatorInput) => {
      const { data } = await axios.put<{ success?: boolean }>(
        `${import.meta.env.VITE_API_URL}/api/v2/users/${userId}/operator-assignment`,
        { dispatchCenterId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["users"] });
      void queryClient.invalidateQueries({ queryKey: ["operators"] });
    },
  });
};

export const useUnassignOperator = () => {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      const { data } = await axios.delete<{ success?: boolean }>(
        `${import.meta.env.VITE_API_URL}/api/v2/users/${userId}/operator-assignment`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["users"] });
      void queryClient.invalidateQueries({ queryKey: ["operators"] });
    },
  });
};

export const useAssignAdmin = () => {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      const { data } = await axios.put<{ success?: boolean }>(
        `${import.meta.env.VITE_API_URL}/api/v2/users/${userId}/admin-role`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
};

export const useUnassignAdmin = () => {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      const { data } = await axios.delete<{ success?: boolean }>(
        `${import.meta.env.VITE_API_URL}/api/v2/users/${userId}/admin-role`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
};

export const useSendPasswordResetEmail = ({
  isCustomer = false,
  onSuccess,
}: {
  isCustomer?: boolean;
  onSuccess?: () => void;
}) => {
  const { token } = useSession();
  return useMutation({
    mutationFn: async (userId: string) => {
      await axios.post<{ success?: boolean }>(
        `${import.meta.env.VITE_API_URL}/api/v2/users/${userId}/send-password-reset-email${
          isCustomer ? "-customer" : ""
        }`,
        {
          userId,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    },
    onSuccess: () => {
      onSuccess?.();
    },
  });
};
